import { useState } from 'react';
import { Pause, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { PCMP_HOLD_ACTION_COPY } from '../../constants/pcmpHold.constants';
import type { PcmpHoldAction } from '../../hooks/useHoldPcmpProposal';
import { PcmpHoldDialog, type PcmpHoldSummary } from './PcmpHoldDialog';

interface PcmpHoldActionButtonsProps {
  proposalId: number;
  refNo: string;
  canHold: boolean;
  canRelease: boolean;
  holdStageLabel?: string | null | undefined;
  hold?: PcmpHoldSummary | null | undefined;
  className?: string;
}

/** Hold and Release for one proposal, from the API's canHold / canRelease. */
export function PcmpHoldActionButtons({
  proposalId,
  refNo,
  canHold,
  canRelease,
  holdStageLabel,
  hold,
  className,
}: PcmpHoldActionButtonsProps) {
  const [open, setOpen] = useState<PcmpHoldAction | null>(null);

  // An open dialog outlives the capabilities, so a 409's message stays readable.
  if (!canHold && !canRelease && !open) return null;

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      {canHold ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => setOpen('HOLD')}
          className="gap-1.5"
        >
          <Pause className="size-3.5" />
          {PCMP_HOLD_ACTION_COPY.HOLD.label}
        </Button>
      ) : null}
      {canRelease ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => setOpen('RELEASE')}
          className="gap-1.5"
        >
          <Play className="size-3.5" />
          {PCMP_HOLD_ACTION_COPY.RELEASE.label}
        </Button>
      ) : null}

      {/* Mounted only while open, so each starts empty. */}
      {open ? (
        <PcmpHoldDialog
          target={{ proposalId, refNo, action: open, holdStageLabel, hold }}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </div>
  );
}
